import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { InlineEditActions } from '@/components/ui/InlineEditActions';
import { Building2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import type { OrganizationContact } from '@/lib/schemas';

/**
 * Data structure for contact edits
 * 9.12a: Used for inline editing
 * v0.14.5 R2: is_primary added so the inline editor (the one Christa uses)
 * can promote/demote directly — previously the only demote path was promoting
 * someone ELSE (the modal had the checkbox; this inline form did not). The
 * backend demotes other primaries atomically on update, so toggling primary
 * here is the single source of truth for the exclusive semantics.
 */
export interface ContactEditData {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  is_primary?: boolean;
}

interface ContactsListProps {
  contacts: OrganizationContact[];
  onAddContact: () => void;
  onDeleteContact: (contactId: string) => void;
  onSetPrimary: (contactId: string) => void;
  // 9.12a: Inline editing props
  onStartEdit?: (contactId: string) => void;
  onSaveEdit?: (contactId: string, data: ContactEditData) => void;
  onCancelEdit?: () => void;
  editingContactId?: string | null;
  className?: string;
}

/**
 * The email to show for a contact: the `emails` array first (a person added through "Add Accounting Contact"
 * has ONLY that), then the older single `email`. SPEC-christa-contacts-identity R1 AC17: a row must never
 * appear without an email just because it was saved through the newer path.
 */
export function contactEmail(contact: { email?: string | null; emails?: string[] | null }): string {
  return (contact.emails ?? []).find((e) => !!e && e.trim()) || contact.email || '';
}

/**
 * Displays a list of organization contacts with add/delete/setPrimary/edit capabilities.
 *
 * Features:
 * - Displays all contacts with name, email, phone, title
 * - Primary contact indicator
 * - "Add Contact" button
 * - "Set as Primary" button (for non-primary contacts)
 * - Delete button for each contact
 * - Contact count display
 * - Empty state when no contacts
 * - 9.12a: Inline editing for contact details
 */
export function ContactsList({
  contacts,
  onAddContact,
  onDeleteContact,
  onSetPrimary,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  editingContactId,
  className,
}: ContactsListProps) {
  const contactCount = contacts.length;
  const hasContacts = contactCount > 0;

  return (
    <div className={className}>
      {hasContacts ? (
        <>
          {/* Header with Add Button */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Contacts</h2>
              <p className="text-sm text-gray-500 mt-1">
                {contactCount} {contactCount === 1 ? 'contact' : 'contacts'}
              </p>
            </div>
            <Button onClick={onAddContact}>Add Contact</Button>
          </div>

          {/* Contacts List */}
          <div className="space-y-4">
            {contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                isEditing={editingContactId === contact.id}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onDeleteContact={onDeleteContact}
                onSetPrimary={onSetPrimary}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Header without Add Button */}
          <div className="mb-4">
            <h2 className="text-xl font-semibold">Contacts</h2>
          </div>

          {/* Empty State with Add Button */}
          <EmptyState
            icon={Building2}
            title="No contacts yet"
            description="Add a primary contact, accounting recipient, or other point of contact"
            actionLabel="Add Contact"
            onAction={onAddContact}
          />
        </>
      )}
    </div>
  );
}

/**
 * Individual contact card with view/edit modes
 * 9.12a: Extracted for cleaner inline editing logic
 */
interface ContactCardProps {
  contact: OrganizationContact;
  isEditing: boolean;
  onStartEdit?: (contactId: string) => void;
  onSaveEdit?: (contactId: string, data: ContactEditData) => void;
  onCancelEdit?: () => void;
  onDeleteContact: (contactId: string) => void;
  onSetPrimary: (contactId: string) => void;
}

function ContactCard({
  contact,
  isEditing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDeleteContact,
  onSetPrimary,
}: ContactCardProps) {
  // Local form state for editing
  const [editForm, setEditForm] = useState<ContactEditData>({
    name: contact.name,
    email: contactEmail(contact),
    phone: contact.phone || '',
    title: contact.title || '',
    is_primary: contact.is_primary_contact || false,
  });

  // Reset form when editing starts or contact changes
  useEffect(() => {
    if (isEditing) {
      setEditForm({
        name: contact.name,
        email: contactEmail(contact),
        phone: contact.phone || '',
        title: contact.title || '',
        is_primary: contact.is_primary_contact || false,
      });
    }
  }, [isEditing, contact]);

  const handleSave = () => {
    if (onSaveEdit) {
      onSaveEdit(contact.id, editForm);
    }
  };

  const handleCancel = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const handleInputChange = (field: keyof ContactEditData, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  if (isEditing) {
    return (
      <div className="border rounded-lg p-4 bg-white shadow-sm ring-2 ring-blue-500">
        <div className="space-y-3">
          {/* Name */}
          <div>
            <label htmlFor={`contact-name-${contact.id}`} className="text-sm font-medium text-gray-700">Name</label>
            <Input
              id={`contact-name-${contact.id}`}
              name="contact-name"
              value={editForm.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Contact name"
              className="mt-1"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor={`contact-email-${contact.id}`} className="text-sm font-medium text-gray-700">Email</label>
            <Input
              id={`contact-email-${contact.id}`}
              name="contact-email"
              type="email"
              value={editForm.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="email@example.com"
              className="mt-1"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor={`contact-phone-${contact.id}`} className="text-sm font-medium text-gray-700">Phone</label>
            <Input
              id={`contact-phone-${contact.id}`}
              name="contact-phone"
              type="tel"
              value={editForm.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="555-1234"
              className="mt-1"
            />
          </div>

          {/* Title */}
          <div>
            <label htmlFor={`contact-title-${contact.id}`} className="text-sm font-medium text-gray-700">Title</label>
            <Input
              id={`contact-title-${contact.id}`}
              name="contact-title"
              value={editForm.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Project Manager"
              className="mt-1"
            />
          </div>

          {/* Primary Contact — v0.14.5 R2: inline demote/promote control.
              Backend demotes other primaries atomically on save, so toggling
              this off yields zero primaries (legal) and toggling it on demotes
              whoever was primary before. No promote-someone-else side effect. */}
          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id={`contact-is-primary-${contact.id}`}
              checked={editForm.is_primary || false}
              onCheckedChange={(checked) =>
                setEditForm(prev => ({ ...prev, is_primary: checked === true }))
              }
            />
            <Label
              htmlFor={`contact-is-primary-${contact.id}`}
              className="text-sm font-medium leading-none"
            >
              Set as primary contact
            </Label>
          </div>

          {/* Save/Cancel Actions */}
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <InlineEditActions
              onCancel={handleCancel}
              showEnterHint={false}
            />
          </div>
        </div>
      </div>
    );
  }

  // View mode
  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Name + Primary Badge */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-medium">{contact.name}</h3>
            {contact.is_primary_contact && (
              <Badge variant="default">Primary</Badge>
            )}
          </div>

          {/* Contact Details */}
          <div className="space-y-1 text-sm text-gray-600">
            {contact.title && <p>{contact.title}</p>}
            {contactEmail(contact) && <p>{contactEmail(contact)}</p>}
            {contact.phone && <p>{contact.phone}</p>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* 9.12a: Edit button */}
          {onStartEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStartEdit(contact.id)}
            >
              Edit
            </Button>
          )}
          {!contact.is_primary_contact && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetPrimary(contact.id)}
            >
              Set as Primary
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDeleteContact(contact.id)}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
